/**
 * BTRADER API Service for frontend integration
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export interface BtraderCredentials {
  api_key: string;
  api_secret: string;
}

export interface VerificationResult {
  success: boolean;
  message: string;
  account_info: {
    fees: Record<string, any>;
    account_type: 'sandbox' | 'production';
    api_permissions: string[];
  } | null;
}

export interface AccountInfo {
  account_info: Record<string, any>;
  api_status: 'connected' | 'disconnected';
  account_type: 'sandbox' | 'production';
}

export interface Balance {
  symbol: string;
  available: string;
  inOrder: string;
}

export interface Market {
  market: string;
  base: string;
  quote: string;
  status: string;
  min_order_in_base_asset: string;
  min_order_in_quote_asset: string;
  price_precision: number;
  base_precision: number;
  quote_precision: number;
}

export interface TickerData {
  market: string;
  ticker: {
    symbol: string;
    priceChange: string;
    priceChangePercent: string;
    weightedAvgPrice: string;
    prevClosePrice: string;
    lastPrice: string;
    lastQty: string;
    bidPrice: string;
    bidQty: string;
    askPrice: string;
    askQty: string;
    openPrice: string;
    highPrice: string;
    lowPrice: string;
    volume: string;
    quoteVolume: string;
    openTime: number;
    closeTime: number;
    count: number;
  };
}

class BtraderService {
  private getAuthHeader(): string {
    const tokensJson = localStorage.getItem('authTokens');
    if (tokensJson) {
      try {
        const tokens = JSON.parse(tokensJson);
        if (tokens.access_token) return `Bearer ${tokens.access_token}`;
      } catch { /* ignore */ }
    }
    throw new Error('Kein Zugriffstoken vorhanden. Bitte melden Sie sich erneut an.');
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}/btrader${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.getAuthHeader(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Verify BTRADER API connection with provided credentials
   */
  async verifyConnection(credentials: BtraderCredentials): Promise<VerificationResult> {
    return this.makeRequest<VerificationResult>('/verify-connection', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  /**
   * Get account information for connected user
   */
  async getAccountInfo(): Promise<AccountInfo> {
    return this.makeRequest<AccountInfo>('/account-info');
  }

  /**
   * Get account balance
   */
  async getBalance(): Promise<{ balances: Balance[]; total_balances: number; active_balances: number }> {
    return this.makeRequest('/balance');
  }

  /**
   * Get available trading markets
   */
  async getMarkets(): Promise<{ markets: Market[]; total_markets: number }> {
    return this.makeRequest('/markets');
  }

  /**
   * Get ticker data for specific market
   */
  async getTicker(market: string): Promise<TickerData> {
    return this.makeRequest(`/ticker/${encodeURIComponent(market)}`);
  }

  /**
   * Disconnect BTRADER API (remove stored credentials)
   */
  async disconnect(): Promise<{ success: boolean; message: string }> {
    return this.makeRequest('/disconnect', {
      method: 'DELETE',
    });
  }

  /**
   * Check if user has BTRADER API connected
   */
  async checkConnection(): Promise<boolean> {
    try {
      const accountInfo = await this.getAccountInfo();
      return accountInfo.api_status === 'connected';
    } catch (error) {
      return false;
    }
  }

  /**
   * Get formatted balance for display
   */
  formatBalance(balance: Balance): {
    symbol: string;
    available: number;
    inOrder: number;
    total: number;
    availableFormatted: string;
    inOrderFormatted: string;
    totalFormatted: string;
  } {
    const available = parseFloat(balance.available);
    const inOrder = parseFloat(balance.inOrder);
    const total = available + inOrder;

    const formatNumber = (num: number): string => {
      return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8,
      }).format(num);
    };

    return {
      symbol: balance.symbol,
      available,
      inOrder,
      total,
      availableFormatted: formatNumber(available),
      inOrderFormatted: formatNumber(inOrder),
      totalFormatted: formatNumber(total),
    };
  }

  /**
   * Format market price data
   */
  formatPrice(price: string | number, precision: number = 2): string {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: precision,
      maximumFractionDigits: 8,
    }).format(numPrice);
  }

  /**
   * Calculate percentage change
   */
  calculatePriceChange(current: string, previous: string): {
    absolute: number;
    percentage: number;
    formatted: string;
  } {
    const currentPrice = parseFloat(current);
    const previousPrice = parseFloat(previous);

    const absolute = currentPrice - previousPrice;
    const percentage = previousPrice !== 0 ? (absolute / previousPrice) * 100 : 0;

    const formattedPercentage = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      signDisplay: 'always',
    }).format(percentage);

    return {
      absolute,
      percentage,
      formatted: `${formattedPercentage}%`,
    };
  }

  /**
   * Validate market symbol format
   */
  validateMarketSymbol(market: string): boolean {
    const marketRegex = /^[A-Z]+-[A-Z]+$/;
    return marketRegex.test(market);
  }

  /**
   * Parse market symbol into base and quote currencies
   */
  parseMarketSymbol(market: string): { base: string; quote: string } | null {
    if (!this.validateMarketSymbol(market)) {
      return null;
    }

    const [base, quote] = market.split('-');
    return { base, quote };
  }

  /**
   * Get popular trading pairs
   */
  getPopularPairs(): string[] {
    return [
      'BTC-EUR',
      'ETH-EUR',
      'ADA-EUR',
      'DOT-EUR',
      'LINK-EUR',
      'LTC-EUR',
      'BCH-EUR',
      'XRP-EUR',
      'BNB-EUR',
      'MATIC-EUR',
    ];
  }

  /**
   * Format error messages for user display
   */
  formatError(error: unknown): string {
    if (error instanceof Error) {
      // Handle specific API error messages
      if (error.message.includes('401')) {
        return 'API-Authentifizierung fehlgeschlagen. Bitte überprüfen Sie Ihre API-Keys.';
      }
      if (error.message.includes('429')) {
        return 'Rate Limit erreicht. Bitte versuchen Sie es später erneut.';
      }
      if (error.message.includes('timeout')) {
        return 'Verbindung zur BTRADER-API ist zeitüberschritten.';
      }
      if (error.message.includes('network') || error.message.includes('fetch')) {
        return 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.';
      }
      return error.message;
    }
    return 'Ein unbekannter Fehler ist aufgetreten.';
  }
}

export const btraderService = new BtraderService();
export default btraderService;